import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { AlertTriangle } from "lucide-react";
import CustomToast from "../../misc/CustomToast";
import TwoSidedSwitch from "../../misc/TwoSidedSwitch";
import type { TwoSidedSwitchSide } from "../../misc/TwoSidedSwitch";
import { getAuthHeader } from "../../../utils/authHeader";
import { getApiErrorMessage } from "../../../utils/apiError";
import type { PropertyGroupPayload } from "../../../types/userListing";
import {
    propertyGroupEffect,
    propertyGroupSiblingLine,
    propertyGroupWhy,
} from "../../../utils/propertyGroupCopy";

interface PropertyGroupNoteProps {
    propertyGroup: PropertyGroupPayload;
    listingId: string;
    canEdit: boolean;
    // Drives the effect sentence: only a confirmed true asserts the Autopilot
    // effect in the present tense (manual campaigns never share posts).
    autopilotEnabled?: boolean | null;
    onChanged: () => Promise<void> | void;
}

// The listing page's explanation of a grouped property and the agent's escape hatch
// (REQ-2): the only recovery for a wrongly-grouped pair that is already imported.
const PropertyGroupNote: React.FC<PropertyGroupNoteProps> = ({
    propertyGroup,
    listingId,
    canEdit,
    autopilotEnabled,
    onChanged,
}) => {
    const [pending, setPending] = useState<boolean | null>(null);
    const [saving, setSaving] = useState(false);
    const separate = pending ?? propertyGroup.marketSeparately;

    const handleChange = async (value: TwoSidedSwitchSide): Promise<void> => {
        const marketSeparately = value === "right";
        // The side labels fire even for the active side; a no-op must not
        // PATCH (group-wide server write) nor claim "Saved".
        if (marketSeparately === separate) return;
        setPending(marketSeparately);
        setSaving(true);
        try {
            const headers = await getAuthHeader();
            await axios.patch(
                `${process.env.REACT_APP_BACKEND_URL}/listing/${listingId}/property-group`,
                { marketSeparately },
                { headers },
            );
            toast.success(
                <CustomToast
                    message="Saved - applies at the next weekly renewal"
                    type="success"
                />,
                { autoClose: 3000 },
            );
            await onChanged();
        } catch (error: unknown) {
            toast.error(
                <CustomToast
                    message={getApiErrorMessage(
                        error,
                        "Failed to update the property grouping.",
                    )}
                    type="error"
                />,
                { autoClose: 3000 },
            );
        } finally {
            setPending(null);
            setSaving(false);
        }
    };

    return (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-secondary bg-secondary/10 p-3 text-left text-xs text-primary">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <div className="flex flex-col gap-1">
                <span>
                    {propertyGroupWhy(propertyGroup)}{" "}
                    {propertyGroupEffect(propertyGroup, autopilotEnabled)}
                </span>
                {propertyGroup.siblings.map((s) => (
                    <span key={s.listingId}>
                        {propertyGroup.marketSeparately
                            ? "Same property as"
                            : "Grouped with"}{" "}
                        {propertyGroupSiblingLine(s)}
                    </span>
                ))}
                {canEdit && (
                    <TwoSidedSwitch
                        id={`property-group-${listingId}`}
                        value={separate ? "right" : "left"}
                        leftLabel="Grouped"
                        rightLabel="Separate"
                        onChange={handleChange}
                        disabled={saving}
                        className="mt-1"
                    />
                )}
            </div>
        </div>
    );
};

export default PropertyGroupNote;
